import { forwardRef } from "react";

import {
  composeRenderProps,
  FieldErrorProps,
  Group,
  InputProps,
  LabelProps,
  FieldError as RACFieldError,
  GroupProps as RACGroupProps,
  Input as RACInput,
  Label as RACLabel,
  Text as RACText,
  TextArea as RACTextarea,
  TextAreaProps,
  TextProps,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { tv } from "tailwind-variants";

import { composeTailwindRenderProps } from "./utils";

export interface IGroupProps extends RACGroupProps {
  size?: "small" | "medium" | "large";
}

export const labelStyles = tv({
  base: "text-pl font-medium cursor-default w-fit transition-colors duration-base ease-out",
  variants: {
    isInvalid: {
      true: "text-contentAlert",
      false: "text-contentPrimary",
    },
    isDisabled: {
      true: "text-contentDisabled opacity-50",
    },
  },
  defaultVariants: {
    isInvalid: false,
    isDisabled: false,
  },
});

export function Label({
  isInvalid,
  isDisabled,
  ...props
}: LabelProps & { isInvalid?: boolean; isDisabled?: boolean }) {
  return (
    <RACLabel
      {...props}
      className={twMerge(
        labelStyles({ isInvalid, isDisabled }),
        props.className,
      )}
    />
  );
}

export function Description(props: TextProps) {
  return (
    <RACText
      {...props}
      slot="description"
      className={twMerge("text-pm text-contentTertiary", props.className)}
    />
  );
}

export function FieldError(props: FieldErrorProps) {
  return (
    <RACFieldError
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        "text-pm text-contentAlert forced-colors:text-[Mark]",
      )}
    />
  );
}

export const fieldBorderStyles = tv({
  base: "rounded-sm",
  variants: {
    isFocusWithin: {
      false:
        "border-2 border-borderSecondary data-hovered:border-borderPrimary",
      true: "border-2 border-borderBrand ring-2 ring-borderFocus/30",
    },
    isInvalid: {
      true: "border-2 border-borderAlert forced-colors:border-borderAlert",
    },
    isDisabled: {
      true: "opacity-60",
    },
  },
});

export const inputStyles = tv({
  base: "text-pl bg-backgroundTertiary text-contentPrimary px-3 flex-1 min-w-0 transition-colors duration-base ease-out outline outline-0 font-regular border-none placeholder:text-contentTertiary min-h-10",
  variants: {
    isInvalid: {
      true: "text-contentAlert placeholder:text-contentAlert",
    },
    isDisabled: {
      true: "text-contentTertiary opacity-60",
    },
  },
});

export const fieldGroupStyles = tv({
  base: "rounded-sm bg-backgroundTertiary w-full group flex items-center transition-colors duration-base ease-out overflow-hidden",
  variants: {
    ...fieldBorderStyles.variants,
    size: {
      small: "min-h-10 max-h-10",
      medium: "px-2 min-h-10",
      large: "px-2 min-h-12",
    },
  },
  defaultVariants: {
    size: "small",
  },
});

export function FieldGroup(props: IGroupProps) {
  return (
    <Group
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        fieldGroupStyles({
          ...renderProps,
          size: props.size,
          className,
        }),
      )}
    />
  );
}

export const Input = forwardRef<
  HTMLInputElement,
  InputProps & { isInvalid?: boolean; isDisabled?: boolean }
>((props, ref) => {
  const { isInvalid, ...restProps } = props;

  return (
    <RACInput
      {...restProps}
      ref={ref}
      className={composeRenderProps(props.className, (className, renderProps) =>
        inputStyles({
          ...renderProps,
          className: twMerge(
            className,
            props.type === "number" &&
              "appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-moz-appearance]:textfield",
          ),
          isInvalid,
        }),
      )}
    />
  );
});

const textAreaBaseClassName = twMerge(
  "block w-full resize-y min-h-[80px] rounded-sm px-3 py-2.5 text-pl font-regular",
  "bg-backgroundTertiary text-contentPrimary placeholder:text-contentTertiary",
  "border-2 border-solid border-borderSecondary",
  "transition-colors duration-base ease-out outline-none",
  "data-hovered:border-borderPrimary hover:border-borderPrimary",
  "data-focused:border-borderBrand data-focused:ring-2 data-focused:ring-borderFocus/30 focus:border-borderBrand focus:outline-none",
  "data-[invalid]:border-borderAlert data-[invalid]:hover:border-borderAlert data-[invalid]:focus:border-borderAlert",
  "aria-invalid:border-borderAlert aria-invalid:hover:border-borderAlert aria-invalid:focus:border-borderAlert",
  "disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:border-borderSecondary",
);

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (props, ref) => {
    return (
      <RACTextarea
        {...props}
        ref={ref}
        className={composeRenderProps(props.className, (className) =>
          twMerge(textAreaBaseClassName, className),
        )}
      />
    );
  },
);
